import { useMemo } from 'react'
import { useTokenPrice } from '~/hooks/useTokenPrice'
import { useLimitSwapLimitPrice } from '~/state/dex/hooks'
import { toBigNumber } from '~/utils/numbers'

interface UseLimitOrderPricesProps {
  inputToken: LimitSwapToken
  outputToken: LimitSwapToken
  isInputRateDirection: boolean
}

export const useLimitOrderMarketRate = (
  inputToken?: LimitSwapToken,
  outputToken?: LimitSwapToken,
) => {
  const inputPrice = useTokenPrice(inputToken?.address)
  const outputPrice = useTokenPrice(outputToken?.address)

  return useMemo(() => {
    const hasValidPrices = Number(inputPrice) && Number(outputPrice)
    if (!hasValidPrices) {
      return {
        marketRate: '0',
        reverseMarketRate: '0',
      }
    }

    try {
      // 计算正向汇率 (inputToken -> outputToken)
      const rate = toBigNumber(inputPrice).div(outputPrice)
      const calcScale = +rate < 1e-6 ? 12 : 6
      const marketRate = rate.decimalPlaces(calcScale).toFixed()

      // 计算反向汇率 (outputToken -> inputToken)
      const reverseRate = toBigNumber(outputPrice).div(inputPrice)
      const reverseCalcScale = +reverseRate < 1e-6 ? 12 : 6
      const reverseMarketRate = reverseRate
        .decimalPlaces(reverseCalcScale)
        .toFixed()

      return {
        marketRate,
        reverseMarketRate,
      }
    } catch (error) {
      console.error('Market rate calculation failed:', error)
      return {
        marketRate: '0',
        reverseMarketRate: '0',
      }
    }
  }, [inputPrice, outputPrice])
}

export const useLimitOrderPrices = ({
  inputToken,
  outputToken,
  isInputRateDirection,
}: UseLimitOrderPricesProps) => {
  const rightToken = isInputRateDirection ? outputToken : inputToken
  const rightTokenPrice = useTokenPrice(rightToken?.address)

  const { marketRate, reverseMarketRate } = useLimitOrderMarketRate(
    inputToken,
    outputToken,
  )
  const displayMarketRate = useMemo(() => {
    return isInputRateDirection ? marketRate : reverseMarketRate
  }, [isInputRateDirection, marketRate, reverseMarketRate])

  const limitPrice = useLimitSwapLimitPrice()

  const displayLimitPrice = useMemo(() => {
    if (limitPrice === '') {
      return ''
    }

    if (isInputRateDirection) {
      const calcScale = +limitPrice < 1e-6 ? 12 : 6
      return toBigNumber(limitPrice).decimalPlaces(calcScale).toFixed()
    } else {
      const isValid = Number(limitPrice) > 0
      if (!isValid) return '0'
      const calcReverse = toBigNumber(1).div(limitPrice)
      const calcScale = +calcReverse < 1e-6 ? 12 : 6
      return calcReverse.decimalPlaces(calcScale).toFixed()
    }
  }, [isInputRateDirection, limitPrice])

  const displayLimitPriceValue = useMemo(() => {
    try {
      const usdValue = toBigNumber(displayLimitPrice)
        .times(rightTokenPrice)
        .toFixed(2)
      const formattedResult = Number(usdValue) ? `(~$${usdValue})` : '(~)'
      return formattedResult
    } catch (error) {
      console.error('USD rate calculation failed:', error)
      return '(~)'
    }
  }, [displayLimitPrice, rightTokenPrice])

  return {
    marketRate,
    displayMarketRate,
    displayLimitPrice,
    displayLimitPriceValue,
  }
}
